import { generateHTML } from "@tiptap/react";
import DOMPurify from "dompurify";
import { useMemo } from "react";
import { blogExtensions } from "@/lib/blog-extensions";
import { isAllowedEmbedSrc, isAllowedVideoSrc } from "@/lib/blog-sanitize";

interface BlogRendererProps {
  content: string | object;
  className?: string;
}

let hookRegistered = false;
function ensureMediaHook() {
  if (hookRegistered) return;
  hookRegistered = true;
  DOMPurify.addHook("uponSanitizeElement", (node, data) => {
    if (data.tagName !== "iframe" && data.tagName !== "video") return;
    const el = node as Element;
    const src = el.getAttribute?.("src");
    const ok = data.tagName === "iframe" ? isAllowedEmbedSrc(src) : isAllowedVideoSrc(src);
    if (!ok) el.parentNode?.removeChild(el);
  });
}

export function BlogRenderer({ content, className }: BlogRendererProps) {
  const html = useMemo(() => {
    ensureMediaHook();
    try {
      const json = typeof content === "string" ? JSON.parse(content) : content;
      return DOMPurify.sanitize(generateHTML(json, blogExtensions), {
        FORBID_ATTR: ["onerror", "onload", "onclick"],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
        ADD_TAGS: ["iframe", "video"],
        ADD_ATTR: ["allowfullscreen", "frameborder", "controls", "preload", "width", "height", "start"],
      });
    } catch {
      // Fallback: render as plain text (escape content to prevent XSS)
      return typeof content === "string"
        ? DOMPurify.sanitize(`<p>${content}</p>`)
        : "";
    }
  }, [content]);

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{
        /* Scoped prose styling for rendered content */
      }}
    />
  );
}
